// pages/ProductFormPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useModal } from '../context/ModalContext';
import API from '../api/MainApi';
import constants from '../config/consts';
import './ProductFormPage.css';

// Значения enum из модели Product.js
const CATEGORIES = constants.CATEGORIES;
const TOY_TYPES = constants.TOY_TYPES;
const CARDS_TYPES = constants.CARDS_TYPES;

const ProductFormPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showConfirm, showAlert } = useModal();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        price: '',
        status: 'active',
        isMany: false,
        category: 'Doodle Toys',
        toyType: 'Разные',
        cardsType: 'Разные',
        hight: ''
    });
    const [previewFile, setPreviewFile] = useState(null);
    const [imageFiles, setImageFiles] = useState([]);
    const [existingImages, setExistingImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');

    useEffect(() => {
        if (isEditMode) {
            loadProduct();
        }
    }, [id]);

    const loadProduct = async () => {
        try {
            const data = await API.getProductById(id);
            const product = data.data;
            setFormData({
                title: product.title,
                description: product.description || '',
                price: product.price || '',
                status: product.status,
                isMany: product.isMany || false,
                category: product.category || 'Doodle Toys',
                toyType: product.toyType || 'Разные',
                cardsType: product.cardsType || 'Разные',
                hight: product.hight || ''
            });
            
            if (product.preViewImg) {
                setPreviewUrl(`${constants.serverUrl}${product.preViewImg}`);
            }
            
            if (product.images) {
                const images = product.images.split(',').filter(img => img);
                setExistingImages(images.map(img => ({
                    url: `${constants.serverUrl}${img}`,
                    path: img
                })));
            }
        } catch (error) {
            showAlert('Ошибка', 'Не удалось загрузить товар', 'error');
            navigate('/products');
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handlePreviewChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPreviewFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleImagesChange = (e) => {
        const files = Array.from(e.target.files);
        setImageFiles(prev => [...prev, ...files]);
    };

    const removeExistingImage = (imagePath) => {
        showConfirm(
            'Удаление изображения',
            'Вы уверены, что хотите удалить это изображение?',
            async () => {
                try {
                    await API.deleteProductImages(id, [imagePath]);
                    setExistingImages(prev => prev.filter(img => img.path !== imagePath));
                    showAlert('Успешно', 'Изображение удалено', 'success');
                } catch (error) {
                    showAlert('Ошибка', 'Не удалось удалить изображение', 'error');
                }
            }
        );
    };

    const removeNewImage = (index) => {
        showConfirm(
            'Удаление изображения',
            'Убрать это изображение из загрузки?',
            () => {
                setImageFiles(prev => prev.filter((_, i) => i !== index));
            }
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title) {
            showAlert('Ошибка', 'Название товара обязательно', 'error');
            return;
        }

        const action = isEditMode ? 'сохранить изменения' : 'создать товар';
        
        showConfirm(
            isEditMode ? 'Сохранение изменений' : 'Создание товара',
            `Вы уверены, что хотите ${action}?`,
            async () => {
                setLoading(true);

                const formDataToSend = new FormData();
                formDataToSend.append('title', formData.title);
                formDataToSend.append('description', formData.description);
                formDataToSend.append('price', formData.price);
                formDataToSend.append('status', formData.status);
                formDataToSend.append('isMany', formData.isMany);
                formDataToSend.append('category', formData.category);
                formDataToSend.append('toyType', formData.toyType);
                formDataToSend.append('cardsType', formData.cardsType);
                if (formData.hight) {
                    formDataToSend.append('hight', formData.hight);
                }

                if (previewFile) {
                    formDataToSend.append('preview', previewFile);
                }

                imageFiles.forEach(file => {
                    formDataToSend.append('images', file);
                });

                try {
                    if (isEditMode) {
                        await API.editProduct(id, formDataToSend);
                        showAlert('Успешно', 'Товар успешно обновлен', 'success');
                    } else {
                        await API.createProduct(formDataToSend);
                        showAlert('Успешно', 'Товар успешно создан', 'success');
                    }
                    navigate('/products');
                } catch (error) {
                    showAlert('Ошибка', error.response?.data?.message || 'Не удалось сохранить товар', 'error');
                } finally {
                    setLoading(false);
                }
            }
        );
    };

    const handleCancel = () => {
        showConfirm(
            'Отмена',
            'Все несохраненные изменения будут потеряны. Продолжить?',
            () => {
                navigate('/products');
            }
        );
    };

    // Определяем, показывать ли селект toyType или cardsType
    const showToyType = formData.category === 'Doodle Toys';
    const showCardsType = formData.category === 'Открытки';

    return (
        <div className="product-form-page">
            <div className="page-header">
                <h1>{isEditMode ? 'Редактирование товара' : 'Создание товара'}</h1>
            </div>

            <form onSubmit={handleSubmit} className="product-form">
                <div className="form-section">
                    <h2>Основная информация</h2>
                    
                    <div className="form-group">
                        <label>Название товара *</label>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Описание</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            rows="5"
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Цена (₽)</label>
                            <input
                                type="number"
                                name="price"
                                value={formData.price}
                                onChange={handleInputChange}
                                min="0"
                                step="0.01"
                            />
                        </div>

                        <div className="form-group">
                            <label>Высота (см)</label>
                            <input
                                type="number"
                                name="hight"
                                value={formData.hight}
                                onChange={handleInputChange}
                                min="0"
                                step="0.1"
                                placeholder="Например: 10.5"
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Категория</label>
                            <select
                                name="category"
                                value={formData.category}
                                onChange={handleInputChange}
                            >
                                {CATEGORIES.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>

                        {showToyType && (
                            <div className="form-group">
                                <label>Тип игрушки</label>
                                <select
                                    name="toyType"
                                    value={formData.toyType}
                                    onChange={handleInputChange}
                                >
                                    {TOY_TYPES.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {showCardsType && (
                            <div className="form-group">
                                <label>Тип открытки</label>
                                <select
                                    name="cardsType"
                                    value={formData.cardsType}
                                    onChange={handleInputChange}
                                >
                                    {CARDS_TYPES.map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Статус</label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                            >
                                <option value="active">Активный</option>
                                <option value="archive">В архиве</option>
                            </select>
                        </div>
                        <div className="form-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    name="isMany"
                                    checked={formData.isMany}
                                    onChange={handleInputChange}
                                />
                                Можно заказать несколько штук
                            </label>
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h2>Изображения</h2>
                    
                    <div className="form-group">
                        <label>Превью изображение</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handlePreviewChange}
                        />
                        {previewUrl && (
                            <div className="preview-image">
                                <img src={previewUrl} alt="Preview" />
                            </div>
                        )}
                    </div>

                    {isEditMode && existingImages.length > 0 && (
                        <div className="form-group">
                            <label>Существующие изображения</label>
                            <div className="images-grid">
                                {existingImages.map((img, index) => (
                                    <div key={index} className="image-item">
                                        <img src={img.url} alt="" />
                                        <button
                                            type="button"
                                            onClick={() => removeExistingImage(img.path)}
                                            className="remove-image"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="form-group">
                        <label>Добавить новые изображения</label>
                        <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImagesChange}
                        />
                        {imageFiles.length > 0 && (
                            <div className="images-grid">
                                {imageFiles.map((file, index) => (
                                    <div key={index} className="image-item">
                                        <img src={URL.createObjectURL(file)} alt="" />
                                        <button
                                            type="button"
                                            onClick={() => removeNewImage(index)}
                                            className="remove-image"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="btn-cancel"
                        disabled={loading}
                    >
                        Отмена
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="btn-submit"
                    >
                        {loading ? 'Сохранение...' : (isEditMode ? 'Сохранить' : 'Создать')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ProductFormPage;